/**
 * Live-agent routing settings and the Available / Away control.
 * Bundled via import from app.js. Uses the global React, same as followups.js.
 *
 * AgentRoutingSettings({ api, toast, tenantId })
 * AgentStatusToggle({ api, toast, user })
 */
const { useEffect, useState } = React;

const CHOICES = [
  {
    id: 'longest_idle',
    title: 'Next available agent (longest idle first)',
    detail: 'The person who has been waiting the longest since their last call gets this caller. Busy, away, and just-finished agents are skipped.'
  },
  {
    id: 'round_robin',
    title: 'Take turns (round robin)',
    detail: 'Each new caller starts with the teammate who has gone the longest without being offered a call.'
  },
  {
    id: 'ring_all',
    title: 'Ring everyone at once',
    detail: 'Every teammate who has the admin open sees the popup together. The first person to accept gets the call.'
  }
];

function messageOf(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function AgentRoutingSettings({ api, toast, tenantId }) {
  const [strategy, setStrategy] = useState('longest_idle');
  const [ring, setRing] = useState(20);
  const [maxWait, setMaxWait] = useState(90);
  const [wrapUp, setWrapUp] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/tenant/settings').then((row) => {
      if (cancelled || !row) return;
      if (row.agent_routing_strategy) setStrategy(row.agent_routing_strategy);
      if (row.agent_ring_timeout_s != null) setRing(row.agent_ring_timeout_s);
      if (row.agent_max_wait_s != null) setMaxWait(row.agent_max_wait_s);
      if (row.agent_wrap_up_s != null) setWrapUp(row.agent_wrap_up_s);
      setError('');
    }).catch((err) => {
      if (!cancelled) setError(messageOf(err, 'Could not load routing settings.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  async function save() {
    setSaving(true);
    setError('');
    try {
      await api('PATCH', '/api/tenant/settings', {
        agent_routing_strategy: strategy,
        agent_ring_timeout_s: Number(ring),
        agent_max_wait_s: Number(maxWait),
        agent_wrap_up_s: Number(wrapUp)
      });
      if (toast) toast('Call routing saved.', 'success');
    } catch (err) {
      const text = messageOf(err, 'Could not save call routing.');
      setError(text);
      if (toast) toast(text, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (!tenantId) {
    return (
      <section className="ar">
        <h3 className="ar-title">When a caller asks for a person</h3>
        <p className="ar-copy">Open a workspace to choose how live calls are routed.</p>
      </section>
    );
  }

  return (
    <section className="ar" aria-busy={loading}>
      <div>
        <h3 className="ar-title">When a caller asks for a person</h3>
        <p className="ar-copy">
          The caller hears hold music while your team is offered the call. If the lead has an assigned owner
          and handoff is set to that owner only, we try them first, then the rest of the team — unless you chose
          ring everyone, which keeps today’s one-queue behavior.
        </p>
      </div>
      <fieldset className="ar-choices" disabled={loading || saving}>
        <legend className="ar-help">Who should get the call?</legend>
        {CHOICES.map((choice) => (
          <label key={choice.id} className="ar-choice">
            <input
              type="radio"
              name="agent-routing-strategy"
              value={choice.id}
              checked={strategy === choice.id}
              onChange={() => setStrategy(choice.id)}
            />
            <span>
              <strong>{choice.title}</strong>
              <span>{choice.detail}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="ar-grid">
        <label className="ar-field">
          Ring each person for (seconds)
          <input
            type="number"
            min="10"
            max="60"
            step="1"
            value={ring}
            disabled={loading || saving || strategy === 'ring_all'}
            onChange={(event) => setRing(event.target.value)}
          />
        </label>
        <label className="ar-field">
          Longest the caller waits (seconds)
          <input
            type="number"
            min="30"
            max="300"
            step="1"
            value={maxWait}
            disabled={loading || saving || strategy === 'ring_all'}
            onChange={(event) => setMaxWait(event.target.value)}
          />
        </label>
        <label className="ar-field">
          Wrap-up after a call (seconds)
          <input
            type="number"
            min="0"
            max="180"
            step="1"
            value={wrapUp}
            disabled={loading || saving || strategy === 'ring_all'}
            onChange={(event) => setWrapUp(event.target.value)}
          />
        </label>
      </div>
      <div className="ar-note">
        <strong>If nobody answers.</strong>
        <p className="ar-copy">
          The caller stays on hold until the max wait, then we transfer them to your backup number.
          If that number is missing or the transfer fails, they come back to the AI, which offers
          a callback. If they hang up while waiting, we stop looking for an agent. When ring-everyone
          is on and nobody has the admin open, the AI offers a callback right away instead of holding.
        </p>
      </div>
      {error ? <p className="ar-error" role="alert">{error}</p> : null}
      <div className="ar-actions">
        <button type="button" className="ar-save" onClick={save} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save call routing'}
        </button>
      </div>
    </section>
  );
}

function AgentStatusToggle({ api, toast, user, liveState, connected }) {
  const [state, setState] = useState('offline');
  const [busyToggle, setBusyToggle] = useState(false);
  const role = user && user.role;
  const canToggle = role === 'agent' || role === 'tenant_admin' || role === 'supervisor' || role === 'super_admin';

  useEffect(() => {
    if (!canToggle) return undefined;
    let cancelled = false;
    api('GET', '/api/agent/presence').then((row) => {
      if (!cancelled && row && row.state) setState(row.state);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [canToggle, user && user.id, liveState === 'busy', connected]);

  if (!canToggle) return null;
  if (connected === false) {
    return (
      <span className="ar-status ar-status--offline" title="Live call events are disconnected. Reconnecting…">
        <span className="ar-dot" aria-hidden="true" />
        Offline
      </span>
    );
  }
  if (liveState === 'busy' && state !== 'busy') {
    return (
      <span className="ar-status ar-status--busy" title="You are on a call">
        <span className="ar-dot" aria-hidden="true" />
        Busy
      </span>
    );
  }

  async function toggle() {
    if (state === 'busy' || busyToggle) return;
    const next = state === 'away' ? 'available' : 'away';
    setBusyToggle(true);
    try {
      const row = await api('POST', '/api/agent/presence', { state: next });
      setState(row && row.state ? row.state : next);
    } catch (err) {
      if (toast) toast(messageOf(err, 'Could not update your status.'), 'error');
    } finally {
      setBusyToggle(false);
    }
  }

  const label = state === 'busy' ? 'Busy' : state === 'away' ? 'Away' : state === 'available' ? 'Available' : 'Offline';
  const tone = state === 'available' ? 'available' : state === 'away' ? 'away' : state === 'busy' ? 'busy' : 'offline';
  const title = state === 'busy'
    ? 'You are on a call'
    : state === 'away'
      ? 'You will not be offered new calls. Click to become available.'
      : 'You can be offered new calls. Click to go away.';

  return (
    <button
      type="button"
      className={'ar-status ar-status--' + tone}
      onClick={toggle}
      disabled={state === 'busy' || busyToggle}
      aria-pressed={state !== 'away'}
      title={title}
    >
      <span className="ar-dot" aria-hidden="true" />
      {label}
    </button>
  );
}

export { AgentRoutingSettings, AgentStatusToggle };
